import axios, { AxiosResponse } from 'axios';

function isLocalDevelopment(): boolean {
  return (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  );
}

// ─── Base Instance ─────────────────────────────────────────────────────────────
const axiosInstance = axios.create({
  baseURL: '/server/sample_node_js_function',
  headers: { 'Content-Type': 'application/json' },
  timeout: 10000,
  withCredentials: true,
});

// ─── Request Interceptor ───────────────────────────────────────────────────────
axiosInstance.interceptors.request.use(
  (config) => {
    // Attach auth token here if needed in future
    // config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Response Interceptor ─────────────────────────────────────────────────────
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    let message =
      'A small error has occurred, causing an interruption of service. Please try again.';

    console.error('API Error:\n', error);

    if (axios.isAxiosError(error) && error.code === 'ERR_NETWORK') {
      message =
        "We're having trouble connecting to the network. Please try again later.";
      return Promise.reject(new Error(message));
    }

    if (error && error.response) {
      const errResp: AxiosResponse = error.response;

      if (errResp && errResp.data) {
        // Extract server-provided message if available
        if (errResp.data.message) {
          message = errResp.data.message;
        } else if (errResp.data.error) {
          message = errResp.data.error;
        }

        // Handle session expiry (440)
        if (
          !isLocalDevelopment() &&
          (errResp.status === 401 ||
            errResp.status === 440 ||
            errResp.data.statusCode === 440)
        ) {
          console.warn('Session expired. Redirecting to login...');
          setTimeout(() => {
            window.location.assign('/__catalyst/auth/login');
          }, 1500);
        }

        // Handle maintenance mode (503)
        if (errResp.data.statusCode === 503) {
          console.warn('Service unavailable. Redirecting...');
          setTimeout(() => {
            window.location.href = '/under-maintenance';
          }, 1500);
        }
      }
    }

    return Promise.reject(new Error(message));
  }
);

export interface CatalystRow {
  _id: string;
  name: string;
  projectCode: string;
  client: string;
  owner: string;
  stage: ProjectStage;
  health: ProjectHealth;
  nextAction?: string;
  summary?: string;
  isArchived?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export type ProjectStage =
  | 'Awarded'
  | 'Mobilisation'
  | 'Execution'
  | 'Snagging'
  | 'Handover'
  | 'Warranty';

export type ProjectHealth = 'On Track' | 'Watch' | 'Blocked';

export interface CreateProjectPayload {
  name: string;
  client: string;
  owner: string;
  stage?: ProjectStage;
  health?: ProjectHealth;
  nextAction?: string;
  summary?: string;
}

export interface UpdateProjectPayload {
  name?: string;
  client?: string;
  owner?: string;
  stage?: ProjectStage;
  health?: ProjectHealth;
  nextAction?: string;
  summary?: string;
}


export const APIGetProjects = async (): Promise<AxiosResponse<CatalystRow[] | { data: CatalystRow[] }>> => {
  return axiosInstance.get('/projects');
};

export const APICreateProject = async (
  payload: CreateProjectPayload
): Promise<AxiosResponse> => {
  return axiosInstance.post('/projects', payload);
};

export const APIUpdateProject = async (
  id: string,
  payload: UpdateProjectPayload
): Promise<AxiosResponse> => {
  return axiosInstance.put(`/projects/${id}`, payload);
};

export const APIArchiveProject = async (id: string): Promise<AxiosResponse> => {
  return axiosInstance.delete(`/projects/${id}`);
};
